import React, { useEffect, useRef, useState } from "react";
import { FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import styles from "./EditSaleOrderPanel.module.css";
import CrossButton from "../Customs/Button/CrossButton";
import ChooseVariantModal from "../pages/Watchlist/ChooseVariantModal";
import { toast } from "react-toastify";
import WebService from "../Services/WebService";
import Swal from "sweetalert2";
import OrderChargesEditor from "./OrderChargesEditor";

// Match SaleOrderDetail's OrderData shape (subset as needed)
export interface SaleOrderItem {
  sale_order_items_id: number;
  product_type: "PRODUCT" | "RAW_MATERIAL";
  product_id: number;
  product_name?: string;
  name?: string;
  quantity?: number;
  height?: number;
  width?: number;
  height_margin?: number;
  width_margin?: number;
  is_sagwan_bidding?: boolean;
  door_config?: boolean;
  is_back_design?: boolean;
  is_back_puc?: boolean;
  [key: string]: any;
}

export interface Invoice {
  id?: number | null;
  invoice_id?: number | null;
  invoice_number?: string | null;
}

export interface OrderDataMin {
  sale_order_id: number;
  user_id: number;
  customer_id: number;
  username: string;
  status: string;
  notes?: string;
  final_amount: number;
  sale_order_items: SaleOrderItem[];
  other_charges?: { description: string; amount: number }[];
  invoice: Invoice | null;
}

interface Props {
  open: boolean;
  order: OrderDataMin;
  onClose: () => void;
  onUpdated?: () => void;
  /** Refresh the order without closing the panel (used after saving other charges). */
  onChanged?: () => void;
}

/** Signed "other charges" on one door line: description + amount (+ adds, − reduces). */
export const OtherChargeEditor: React.FC<{
  saleOrderId: number;
  item: SaleOrderItem;
  editable: boolean;
  onSaved: () => void;
}> = ({ saleOrderId, item, editable, onSaved }) => {
  const savedAmount = item.additional_charge != null ? String(item.additional_charge) : "";
  const savedNote = item.additional_charge_note || "";
  const [amount, setAmount] = useState(savedAmount);
  const [note, setNote] = useState(savedNote);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAmount(savedAmount);
    setNote(savedNote);
  }, [savedAmount, savedNote]);

  if (!editable) {
    if (!savedAmount) return null;
    const n = Number(savedAmount);
    return (
      <div className={styles.itemBreakdown}>
        Other charges: {savedNote} ({n < 0 ? "−" : "+"}₹{Math.abs(n).toLocaleString("en-IN")})
      </div>
    );
  }

  const dirty = amount !== savedAmount || note !== savedNote;
  const save = async () => {
    const n = amount.trim() === "" ? 0 : Number(amount);
    if (!Number.isFinite(n)) return toast.error("Enter a valid amount.");
    if (n !== 0 && !note.trim()) return toast.error("Add a description for the charge.");
    setSaving(true);
    try {
      await WebService.patchAPI({
        action: `/v1/door-management/saleorders/${saleOrderId}/item/${item.sale_order_items_id}/additional-charge`,
        body: { additional_charge: n, additional_charge_note: note.trim() },
      });
      toast.success(n === 0 ? "Other charges removed." : "Other charges saved. Total updated.");
      onSaved();
    } catch {
      // WebService shows the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.chargeRow}>
      <input
        className={styles.chargeNote}
        placeholder="Other charges (e.g. Polish, Hardware)"
        value={note}
        maxLength={120}
        onChange={(e) => setNote(e.target.value)}
      />
      <input
        className={styles.chargeAmount}
        type="number"
        step="0.01"
        placeholder="± Amount"
        title="Positive adds to the total, negative reduces it"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      <button type="button" className={styles.chargeSave} disabled={!dirty || saving} onClick={save}>
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
};

const NO_IMAGE = "/Assets/no-pictures.png";

const EditSaleOrderPanel: React.FC<Props> = ({ open, order, onClose, onUpdated, onChanged }) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<SaleOrderItem[]>([]);
  const [editingItem, setEditingItem] = useState<SaleOrderItem | null>(null);
  const [updatingQty, setUpdatingQty] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setItems(Array.isArray(order?.sale_order_items) ? order.sale_order_items : []);
    setEditingItem(null);
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, order?.sale_order_items]);

  if (!open) return null;

  // Size with margin merged, e.g. 84 + 0.5 -> "84.5"
  const fmtDim = (n?: number, m?: number) =>
    typeof n === "number" && n !== 0
      ? String(Math.round((n + (typeof m === "number" ? m : 0)) * 100) / 100)
      : "—";
  const yesNo = (v?: boolean) => (typeof v === "boolean" ? (v ? "Yes" : "No") : "—");
  const inr = (n: number) => "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2 });
  const canEdit = ["PENDING", "IN_PROCESSING", "PLACED"].includes(order?.status || "")
    && !(order?.invoice?.id || order?.invoice?.invoice_id);

  const handleEditItem = (item: SaleOrderItem) => {
    setEditingItem(item);
  };

  const handleUpdateQuantity = async (item: SaleOrderItem, newQuantity: number) => {
    if (!canEdit || newQuantity < 1) return;
    
    setUpdatingQty(item.sale_order_items_id);
    try {
      const payload: any = {
        sale_order_items_id: item.sale_order_items_id,
        sale_order_id: order.sale_order_id,
        product_id: item.product_id,
        product_type: item.product_type,
        quantity: newQuantity,
        height: item.height,
        width: item.width,
        height_margin: item.height_margin,
        width_margin: item.width_margin,
        mica_id: item.mica_id,
        color: item.color,
        door_config: item.door_config,
        is_sagwan_bidding: item.is_sagwan_bidding,
        is_back_design: item.is_back_design,
        is_back_puc: item.is_back_puc,
        wood_quality_id: item.wood_quality_id,
        packaging_type_id: item.packaging_type_id,
        description: item.description,
        product_name: item.product_name || item.name,
        product_description: item.product_description,
        unit_price: item.unit_price ?? 0,
        discount: item.discount ?? 0,
        total_price: item.total_price ?? 0,
      };

      const res = await WebService.postAPI({
        action: `/v1/door-management/saleorders/${order.sale_order_id}/user/${order.user_id}/item`,
        body: payload,
      }) as { status_code?: number; message?: string };

      if ([200, 201].includes(res.status_code ?? 0)) {
        toast.success("Quantity updated!");
        if (onUpdated) onUpdated();
      } else {
        toast.error(res?.message || "Failed to update quantity.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Network error.");
    } finally {
      setUpdatingQty(null);
    }
  };

  const handleDeleteItem = async (item: SaleOrderItem) => {
    if (!canEdit) {
      toast.error("Cannot delete items in this order state.");
      return;
    }

    const confirm = await Swal.fire({
      title: "Remove item?",
      text: "Are you sure you want to remove this item from the order?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E13D22",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      heightAuto: false,
    });

    if (!confirm.isConfirmed) return;

    setDeleting(item.sale_order_items_id);
    try {
      const res = await WebService.deleteAPI({
        action: `/v1/door-management/saleorders/${order.sale_order_id}/user/${order.user_id}/item/${item.sale_order_items_id}`,
      }) as { status_code?: number; message?: string };

      if ([200, 201].includes(res.status_code ?? 0)) {
        toast.success("Item removed!");
        if (onUpdated) onUpdated();
      } else {
        toast.error(res?.message || "Failed to delete item.");
      }
    } catch (err: any) {
      toast.error(err?.message || "Network error.");
    } finally {
      setDeleting(null);
    }
  };

  const handleAddMore = () => {
    // Opens shop in a new window to add more items
    const shopUrl = "/shop";
    window.open(shopUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.panel} ref={panelRef}>
        <div className={styles.header}>
          <div className={styles.heading}>Edit Order #{order?.sale_order_id}</div>
          <CrossButton onClick={onClose} className={styles.closeBtn} />
        </div>

        {!canEdit && (
          <div className={styles.notice}>
            ⚠️ This order cannot be edited (either shipped/completed or invoice exists).
          </div>
        )}

        <div className={styles.section}>
          <div className={styles.metaGrid}>
            <div className={styles.metaItem}>
              <div className={styles.label}>Customer</div>
              <div className={styles.value}>{order?.username || "—"}</div>
            </div>
            <div className={styles.metaItem}>
              <div className={styles.label}>Status</div>
              <div className={styles.value}>{order?.status || "—"}</div>
            </div>
            <div className={styles.metaItem}>
              <div className={styles.label}>Invoice</div>
              <div className={styles.value}>
                {order?.invoice?.invoice_number
                  || (order?.invoice?.id || order?.invoice?.invoice_id
                    ? `#${order.invoice.id ?? order.invoice.invoice_id}`
                    : "Not Generated")}
              </div>
            </div>
            <div className={styles.metaItem}>
              <div className={styles.label}>Total Amount</div>
              <div className={styles.value}>{inr(Number(order?.final_amount || 0))}</div>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div className={styles.sectionTitle}>Order Items</div>
            {canEdit && (
              <button
                className={styles.addMoreBtn}
                onClick={handleAddMore}
                type="button"
                title="Add more items from shop"
              >
                <FaPlus style={{ marginRight: 6 }} />
                Add More
              </button>
            )}
          </div>
          <div className={styles.itemsBox}>
            {items.length === 0 ? (
              <div className={styles.hint}>No items found in this order.</div>
            ) : (
              items.map((it) => {
                const displayName = it.product_name || it.name || (it.product && it.product.name) || "Product";
                const isUpdating = updatingQty === it.sale_order_items_id;
                const isDeleting = deleting === it.sale_order_items_id;
                const qty = it.quantity ?? 0;
                
                return (
                  <div key={it.sale_order_items_id} className={styles.item}>
                    <img alt={displayName} src={NO_IMAGE} className={styles.thumb} />
                    <div className={styles.itemInfo}>
                      <div className={styles.itemName}>{displayName}</div>
                      <div className={styles.itemMeta}>
                        Type: <span className={styles.badge}>{it.product_type}</span> • 
                        Sagwan: {yesNo(it.is_sagwan_bidding)} • Double Door: {yesNo(it.door_config)} • 
                        Back Design: {yesNo(it.is_back_design)} • B-PVC: {yesNo(it.is_back_puc)}
                      </div>
                      <div className={styles.itemBreakdown}>
                        Height: {fmtDim(it.height, it.height_margin)} • Width: {fmtDim(it.width, it.width_margin)}
                      </div>
                      <OtherChargeEditor
                        saleOrderId={order.sale_order_id}
                        item={it}
                        editable={false}
                        onSaved={() => (onChanged ? onChanged() : onUpdated?.())}
                      />
                    </div>
                    <div className={styles.itemActions}>
                      {canEdit && (
                        <>
                          <button
                            className={styles.qtyBtn}
                            onClick={() => handleUpdateQuantity(it, qty - 1)}
                            disabled={qty <= 1 || isUpdating}
                            title="Decrease quantity"
                          >
                            -
                          </button>
                          <span className={styles.qtyValue}>{qty}</span>
                          <button
                            className={styles.qtyBtn}
                            onClick={() => handleUpdateQuantity(it, qty + 1)}
                            disabled={isUpdating}
                            title="Increase quantity"
                          >
                            +
                          </button>
                          <FaEdit
                            className={styles.editIcon}
                            onClick={() => !isUpdating && !isDeleting && handleEditItem(it)}
                            title="Edit item"
                            style={{ marginLeft: 8, cursor: isUpdating || isDeleting ? 'not-allowed' : 'pointer', opacity: isUpdating || isDeleting ? 0.5 : 1 }}
                          />
                          <FaTrash
                            className={styles.deleteIcon}
                            onClick={() => !isUpdating && !isDeleting && handleDeleteItem(it)}
                            title="Delete item"
                            style={{ marginLeft: 8, cursor: isUpdating || isDeleting ? 'not-allowed' : 'pointer', opacity: isUpdating || isDeleting ? 0.5 : 1 }}
                          />
                        </>
                      )}
                      {!canEdit && (
                        <div className={styles.qtyLabel}>Qty: {qty}</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
          <div className={styles.hint}>
            💡 Edit items to change variants, quantity, or dimensions. Changes are shown live above.
          </div>
        </div>

        <div className={styles.section}>
          <OrderChargesEditor
            saleOrderId={order.sale_order_id}
            charges={order.other_charges}
            editable={canEdit}
            onSaved={() => (onChanged ? onChanged() : onUpdated?.())}
          />
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>Close</button>
          <button className={styles.saveBtn} onClick={onUpdated} disabled={!canEdit}>
            Save Changes
          </button>
        </div>

        {/* Variant Modal */}
        {editingItem && (
          <ChooseVariantModal
            onClose={() => setEditingItem(null)}
            cartItem={{
              ...editingItem,
              cart_item_id: editingItem.sale_order_items_id,
              color: (editingItem as any).color || "",
              wood_quality_id: (editingItem as any).wood_quality_id || "",
              packaging_type_id: (editingItem as any).packaging_type_id || 0,
              description: (editingItem as any).description || "",
              PRODUCT: {
                id: editingItem.product_id,
                name: editingItem.product_name || editingItem.name || "",
                description: "",
                images: [],
                sku_id: "",
                rating: 0,
                review_count: 0,
              },
              product_name: editingItem.product_name || editingItem.name || "",
              product_description: "",
            } as any}
            onUpdated={() => {
              setEditingItem(null);
              if (onUpdated) {
                onUpdated(); // trigger parent refresh to pull latest order data
              }
            }}
            itemType={editingItem.product_type === "RAW_MATERIAL" ? "RAW_MATERIAL" : "PRODUCT"}
            customerId={order?.user_id}
            saleOrderId={order?.sale_order_id}
            userId={order?.user_id}
            isSaleOrderEdit={true}
          />
        )}
      </div>
    </div>
  );
};

export default EditSaleOrderPanel;
